import { Box, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import { CustomerOrderAddressCell } from "../components/CustomerOrderListPage/CustomerOrderAddressCell"
import { CustomerOrderSalesOrderCountCell } from "../components/CustomerOrderListPage/CustomerOrderSalesOrderCountCell"

import { Button } from "@/components/common/Button"
import type { CustomerLocationCombination } from "@/graphql/codegen/graphql"

interface UseCustomerOrderColumnsProps {
  onStartOrder: (row: CustomerLocationCombination) => void
  canStartOrder: boolean
}

export const useCustomerOrderColumns = ({
  onStartOrder,
  canStartOrder,
}: UseCustomerOrderColumnsProps): ColumnDef<CustomerLocationCombination>[] => [
  {
    accessorKey: "customer",
    header: "Customer",
    cell: ({ row }) => row.original.company.name,
    enableSorting: true,
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "shipToDescription",
    header: "Customer Location Description",
    cell: ({ row }) => row.original.shipToLocation.description ?? row.original.shipToLocation.name,
    size: 328,
    enableSorting: true,
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "shipTo",
    header: "Ship To",
    cell: ({ row }) => (
      <CustomerOrderAddressCell
        location={row.original.shipToLocation}
        testId={`customer-orders.list-page.ship-to-cell-${row.original.combinationId}`}
      />
    ),
    enableSorting: true,
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "billTo",
    header: "Bill To",
    cell: ({ row }) => (
      <CustomerOrderAddressCell
        location={row.original.billingLocation}
        testId={`customer-orders.list-page.bill-to-cell-${row.original.combinationId}`}
      />
    ),
    enableSorting: true,
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "salesOrders",
    header: "Sales Orders",
    cell: ({ row }) => (
      <CustomerOrderSalesOrderCountCell
        count={row.original.salesOrderCount}
        testId={`customer-orders.list-page.sales-order-count-${row.original.combinationId}`}
      />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "startOrder",
    header: "",
    size: 147,
    enableSorting: false,
    cell: ({ row }) => (
      <Button
        appearance='contained'
        disabled={!canStartOrder}
        onClick={() => onStartOrder(row.original)}
        data-testid={`customer-orders.list-page.start-order-button-${row.original.combinationId}`}
      >
        Start Order
      </Button>
    ),
  },
  {
    accessorKey: "expander",
    header: "",
    size: 64,
    enableSorting: false,
    cell: ({ row }) => {
      const isExpanded = row.getIsExpanded()
      return (
        <Box className='flex items-center justify-end'>
          <IconButton
            size='small'
            onClick={row.getToggleExpandedHandler()}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse sales orders" : "Expand sales orders"}
            className={isExpanded ? "rotate-180 transition-transform" : "transition-transform"}
            data-testid={`customer-orders.list-page.expander-${row.original.combinationId}`}
          >
            <KeyboardArrowDownOutlined />
          </IconButton>
        </Box>
      )
    },
  },
]
